/**
 * Shared 8 cm × 8 cm QR canvas renderer for VB labels.
 * Mirrors backend/core/qr.py token format (`VBE1:<base64url-json>`).
 *
 * Builds an opaque token (kind + id) so default phone cameras see noise,
 * not a URL — only the in-app scanner can decode and route.
 */
import QRCode from "qrcode";

const PX_PER_CM = 118.11;            // 300 DPI
const QR_SIZE_CM = 8;
export const QR_SIZE_PX = Math.round(QR_SIZE_CM * PX_PER_CM);  // ≈945 px
const LABEL_BAND_PX = 240;
export const QR_CANVAS_W = QR_SIZE_PX;
export const QR_CANVAS_H = QR_SIZE_PX + LABEL_BAND_PX;

/** Build the opaque VBE1 token for a (kind, id) pair. */
export function buildToken(kind, id) {
  const json = JSON.stringify({ t: kind, id });
  const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `VBE1:${b64}`;
}

/**
 * Render a labeled QR onto the supplied canvas at print scale (8×8 cm).
 * `lines` is an array of label strings drawn under the QR (top-down).
 * Each line gets a font/colour appropriate to its position.
 */
export async function renderQRLabel(canvas, token, lines = []) {
  canvas.width = QR_CANVAS_W;
  canvas.height = QR_CANVAS_H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, QR_CANVAS_W, QR_CANVAS_H);

  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, token, {
    errorCorrectionLevel: "H",
    margin: 2,
    width: QR_SIZE_PX,
    color: { dark: "#000000", light: "#ffffff" },
  });
  ctx.drawImage(qrCanvas, 0, 0, QR_SIZE_PX, QR_SIZE_PX);

  ctx.textAlign = "center";

  const styles = [
    { font: "bold 64px 'Helvetica Neue', Arial, sans-serif", fill: "#000000", offset: 90 },
    { font: "500 36px 'Helvetica Neue', Arial, sans-serif",  fill: "#333333", offset: 165 },
    { font: "400 28px 'Helvetica Neue', Arial, sans-serif",  fill: "#666666", offset: 215 },
  ];
  lines.slice(0, 3).forEach((text, i) => {
    if (!text) return;
    const s = styles[i];
    ctx.font = s.font;
    ctx.fillStyle = s.fill;
    drawClamped(ctx, text, QR_CANVAS_W / 2, QR_SIZE_PX + s.offset, QR_CANVAS_W - 80);
  });
}

/**
 * Render a QR sticker sized for the 80×60 mm thermal roll (203 DPI by default).
 * QR on top, up to 3 caption lines below — same content as `renderQRLabel`,
 * but scaled to physical label dimensions for TSPL thermal printing.
 */
export async function renderQRSticker(canvas, token, lines = [], opts = {}) {
  const { widthMm = 80, heightMm = 60, dpi = 203, rotate = false } = opts;
  const MM = 25.4;
  const W = Math.round((widthMm * dpi) / MM);
  const H = Math.round((heightMm * dpi) / MM);

  // When rotating, lay out in the swapped orientation then rotate onto W×H.
  const LW = rotate ? H : W;
  const LH = rotate ? W : H;

  const content = document.createElement("canvas");
  content.width = LW;
  content.height = LH;
  const ctx = content.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, LW, LH);

  const pad = Math.round(Math.min(LW, LH) * 0.05);
  const band = Math.round(LH * 0.26);
  const qr = Math.min(LH - band - pad * 2, LW - pad * 2);
  const qrX = Math.round((LW - qr) / 2);
  const qrY = pad;
  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, token, {
    errorCorrectionLevel: "H", margin: 2, width: qr,
    color: { dark: "#000000", light: "#ffffff" },
  });
  ctx.drawImage(qrCanvas, qrX, qrY, qr, qr);

  ctx.textAlign = "center";
  const base = qrY + qr;
  // Fixed styles per line. `dy` is the baseline for the *first* visual
  // line of that block; if a block wraps to 2 lines the second line
  // pushes subsequent blocks down proportionally.
  const styles = [
    { font: `bold ${Math.round(LH * 0.072)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#000000", dy: Math.round(LH * 0.085) },
    { font: `500 ${Math.round(LH * 0.05)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#333333", dy: Math.round(LH * 0.06) },
    { font: `400 ${Math.round(LH * 0.042)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#666666", dy: Math.round(LH * 0.052) },
  ];
  let cursorY = base;
  lines.slice(0, 3).forEach((text, i) => {
    if (!text) return;
    const s = styles[i];
    ctx.font = s.font;
    ctx.fillStyle = s.fill;
    cursorY += s.dy;
    const { lines: used, size } = drawWrapped(ctx, text, LW / 2, cursorY, LW - pad * 2);
    if (used > 1) cursorY += Math.round(size * 1.1);
  });

  canvas.width = W;
  canvas.height = H;
  const octx = canvas.getContext("2d");
  octx.fillStyle = "#ffffff";
  octx.fillRect(0, 0, W, H);
  if (rotate) {
    octx.save();
    octx.translate(W, 0);
    octx.rotate(Math.PI / 2);
    octx.drawImage(content, 0, 0);
    octx.restore();
  } else {
    octx.drawImage(content, 0, 0);
  }
}

/**
 * Render the CUSTOMER LOGIN QR sticker.
 * Unlike the opaque VBE1 stickers, the QR here encodes a plain URL so a normal
 * phone camera can open the login page directly. Sized to physical label
 * dimensions (mm @ dpi); optional 90° rotation to fit a bigger QR on wide labels.
 */
export async function renderLoginSticker(canvas, url, lines = [], opts = {}) {
  const { widthMm = 30, heightMm = 30, dpi = 203, rotate = false } = opts;
  const MM = 25.4;
  const W = Math.round((widthMm * dpi) / MM);
  const H = Math.round((heightMm * dpi) / MM);

  // Layout is drawn into a canvas whose orientation matches the drawing space:
  // when rotating we lay out in the swapped (H×W) space, then rotate onto W×H.
  const LW = rotate ? H : W;
  const LH = rotate ? W : H;

  const content = document.createElement("canvas");
  content.width = LW;
  content.height = LH;
  const cctx = content.getContext("2d");
  cctx.fillStyle = "#ffffff";
  cctx.fillRect(0, 0, LW, LH);

  const pad = Math.round(Math.min(LW, LH) * 0.05);
  const band = Math.round(LH * 0.24);
  const qr = Math.min(LH - band - pad * 2, LW - pad * 2);
  const qrX = Math.round((LW - qr) / 2);
  const qrY = pad;
  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, url, {
    errorCorrectionLevel: "M", margin: 2, width: qr,
    color: { dark: "#000000", light: "#ffffff" },
  });
  cctx.drawImage(qrCanvas, qrX, qrY, qr, qr);

  cctx.textAlign = "center";
  const base = qrY + qr;
  const styles = [
    { font: `bold ${Math.round(LH * 0.066)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#000000", dy: Math.round(LH * 0.08) },
    { font: `500 ${Math.round(LH * 0.046)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#333333", dy: Math.round(LH * 0.06) },
    { font: `400 ${Math.round(LH * 0.04)}px 'Helvetica Neue', Arial, sans-serif`, fill: "#666666", dy: Math.round(LH * 0.045) },
  ];
  let cursorY = base;
  lines.slice(0, 3).forEach((text, i) => {
    if (!text) return;
    const s = styles[i];
    cctx.font = s.font;
    cctx.fillStyle = s.fill;
    cursorY += s.dy;
    const { lines: used, size } = drawWrapped(cctx, text, LW / 2, cursorY, LW - pad * 2);
    if (used > 1) cursorY += Math.round(size * 1.1);
  });

  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  if (rotate) {
    ctx.save();
    ctx.translate(W, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(content, 0, 0);
    ctx.restore();
  } else {
    ctx.drawImage(content, 0, 0);
  }
}

/** Trigger a PNG download of the canvas as `<safe-name>.png`. */
export function downloadCanvasPNG(canvas, filename) {
  const safe = filename.replace(/[^a-z0-9-_]+/gi, "_").slice(0, 80);
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${safe}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, "image/png");
}

/**
 * Open a printable window with the canvas as a single 8×8 cm sticker
 * (auto-prints when image loads). Caller passes a label for the page title.
 */
export function printCanvas(canvas, title = "QR label") {
  const dataUrl = canvas.toDataURL("image/png");
  const w = window.open("", "_blank", "width=600,height=720");
  if (!w) return;
  const doc = w.document;
  doc.open();
  doc.write("<!doctype html><html><head></head><body></body></html>");
  doc.close();
  doc.title = title;
  const style = doc.createElement("style");
  style.textContent = `
    @page { size: A4; margin: 12mm; }
    body { font-family: -apple-system, Helvetica, Arial, sans-serif; margin: 0; }
    .sheet { display: flex; justify-content: center; padding-top: 12mm; }
    img.qr {
      max-width: 80mm;
      max-height: 80mm;
      width: auto;
      height: auto;
      display: block;
      image-rendering: pixelated;
    }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;
  doc.head.appendChild(style);
  const sheet = doc.createElement("div");
  sheet.className = "sheet";
  const img = doc.createElement("img");
  img.className = "qr";
  img.src = dataUrl;
  img.alt = title;
  sheet.appendChild(img);
  doc.body.appendChild(sheet);
  const trigger = () => { try { w.print(); } catch (e) { console.debug("Print dialog dismissed:", e); } };
  if (img.complete) trigger();
  else img.onload = trigger;
}

/**
 * Print MULTIPLE QRs to one A4 sheet — used by "Print all" on the Sites page.
 * `items` = [{ canvas, title, subtitle }, …]
 */
export function printCanvases(items, pageTitle = "QR labels") {
  const cards = items.map(({ canvas, title, subtitle }) => ({
    dataUrl: canvas.toDataURL("image/png"),
    title: String(title || ""),
    subtitle: subtitle ? String(subtitle) : "",
  }));
  const w = window.open("", "_blank", "width=820,height=920");
  if (!w) return;
  const doc = w.document;
  doc.open();
  doc.write("<!doctype html><html><head></head><body></body></html>");
  doc.close();
  doc.title = pageTitle;
  const style = doc.createElement("style");
  style.textContent = `
    @page { size: A4; margin: 10mm; }
    body { font-family: -apple-system, Helvetica, Arial, sans-serif; margin: 0; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; padding: 4mm; }
    .card { border: 1px dashed #999; border-radius: 6px; padding: 6mm; text-align: center; page-break-inside: avoid; }
    img.qr {
      max-width: 80mm;
      max-height: 80mm;
      width: auto;
      height: auto;
      display: block;
      margin: 0 auto;
      image-rendering: pixelated;
    }
    .cap { margin-top: 4mm; font-size: 12pt; line-height: 1.4; }
    .cap span { color: #555; font-size: 10pt; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;
  doc.head.appendChild(style);

  const grid = doc.createElement("div");
  grid.className = "grid";
  const imgs = [];
  cards.forEach(({ dataUrl, title, subtitle }) => {
    const card = doc.createElement("div");
    card.className = "card";
    const img = doc.createElement("img");
    img.className = "qr";
    img.src = dataUrl;
    img.alt = title;
    imgs.push(img);
    card.appendChild(img);
    const cap = doc.createElement("div");
    cap.className = "cap";
    const strong = doc.createElement("strong");
    strong.textContent = title;
    cap.appendChild(strong);
    if (subtitle) {
      cap.appendChild(doc.createElement("br"));
      const sub = doc.createElement("span");
      sub.textContent = subtitle;
      cap.appendChild(sub);
    }
    card.appendChild(cap);
    grid.appendChild(card);
  });
  doc.body.appendChild(grid);

  let pending = imgs.length;
  const trigger = () => { try { w.print(); } catch (e) { console.debug("Print dialog dismissed:", e); } };
  if (pending === 0) trigger();
  imgs.forEach((img) => {
    if (img.complete) {
      pending -= 1;
      if (pending === 0) trigger();
    } else {
      img.onload = () => {
        pending -= 1;
        if (pending === 0) trigger();
      };
    }
  });
}

function drawClamped(ctx, text, x, y, maxWidth) {
  const original = ctx.font;
  let size = parseInt(original.match(/(\d+)px/)?.[1] || "40", 10);
  while (size > 16 && ctx.measureText(text).width > maxWidth) {
    size -= 2;
    ctx.font = original.replace(/\d+px/, `${size}px`);
  }
  ctx.fillText(text, x, y);
  ctx.font = original;
}

/**
 * Draw `text` at (x, y) centred, wrapping to a SECOND line if it
 * doesn't fit on one. Prefers to keep the intended font size, but
 * falls back to progressively smaller sizes when a 2-line wrap still
 * overflows the label — shrinking is much easier to read than the
 * ellipsized cutoff we used to get on labels like "PRODUCTION
 * CHANGEOVER INSPECTION FORM" @ 60×40 mm rotated.
 *
 * Algorithm (per call):
 *   1. If the whole string fits on one line at the intended size,
 *      draw it and return.
 *   2. Else try to split at the longest whitespace prefix that fits.
 *      If the trailing piece also fits maxWidth → draw two lines.
 *   3. Else drop the font size by 2 px and retry from step 1 (down
 *      to 12 px). Titles at 60×40 mm rotated typically need one or
 *      two shrinks to fit an eight-word form name.
 *   4. If even 12 px can't fit on two lines, ellipsize the tail — a
 *      last-resort so we NEVER draw past the label boundary.
 *
 * Returns `{ lines, size }` so the caller can advance its Y cursor
 * by the amount actually rendered.
 */
function drawWrapped(ctx, text, x, y, maxWidth) {
  const original = ctx.font;
  const originalSize = parseInt(original.match(/(\d+)px/)?.[1] || "20", 10);
  const MIN_SIZE = 12;

  for (let size = originalSize; size >= MIN_SIZE; size -= 2) {
    ctx.font = original.replace(/\d+px/, `${size}px`);

    // Attempt 1: whole string on one line.
    if (ctx.measureText(text).width <= maxWidth) {
      ctx.fillText(text, x, y);
      ctx.font = original;
      return { lines: 1, size };
    }

    // Attempt 2: split into 2 lines at the longest whitespace prefix.
    const words = text.split(/\s+/).filter(Boolean);
    let l1 = "", l2 = "";
    for (let i = words.length - 1; i >= 1; i--) {
      const cand = words.slice(0, i).join(" ");
      if (ctx.measureText(cand).width <= maxWidth) {
        l1 = cand;
        l2 = words.slice(i).join(" ");
        break;
      }
    }
    // If a single word overflows the label, fall through to shrink;
    // we only accept an all-word split so hyphenless words never get
    // broken mid-glyph when a smaller font would let them stay whole.
    if (l1 && ctx.measureText(l2).width <= maxWidth) {
      ctx.fillText(l1, x, y);
      ctx.fillText(l2, x, y + Math.round(size * 1.1));
      ctx.font = original;
      return { lines: 2, size };
    }
    // Didn't fit at this size — shrink and try again.
  }

  // ── Last resort ── even 12 px doesn't fit. Hard-break and ellipsize
  // so text stays inside the label rather than bleeding past the edge.
  const size = MIN_SIZE;
  ctx.font = original.replace(/\d+px/, `${size}px`);
  let cut = text.length;
  while (cut > 1 && ctx.measureText(text.slice(0, cut)).width > maxWidth) cut--;
  const head = text.slice(0, cut);
  let tail = text.slice(cut).replace(/^\s+/, "");
  if (ctx.measureText(tail).width > maxWidth) {
    while (tail.length > 1 && ctx.measureText(tail + "…").width > maxWidth) {
      tail = tail.slice(0, -1);
    }
    tail = tail + "…";
  }
  ctx.fillText(head, x, y);
  if (tail) ctx.fillText(tail, x, y + Math.round(size * 1.1));
  ctx.font = original;
  return { lines: tail ? 2 : 1, size };
}
